import { useState } from 'react';
import { Activity, Layers, Loader2, Mail, Send } from 'lucide-react';
import type { PorPunto } from './tiposDelProcesoSri';

export function Pestania({
  activa,
  alPulsar,
  icono: Icono,
  rotulo,
  insignia,
}: {
  activa: boolean;
  alPulsar: () => void;
  icono: typeof Activity;
  rotulo: string;
  insignia?: string | undefined;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      className={`flex items-center gap-2 border-b-2 px-5 py-3 text-[11px] font-bold uppercase tracking-wide transition ${
        activa
          ? 'border-marca-500 text-marca-700'
          : 'border-transparent text-armazon-400 hover:text-armazon-600'
      }`}
    >
      <Icono className="size-4" aria-hidden />
      {rotulo}
      {insignia && (
        <span className="rounded-full bg-marca-100 px-1.5 py-0.5 text-[9px] font-bold text-marca-700">
          {insignia}
        </span>
      )}
    </button>
  );
}

export function RepartoPorPunto({ reparto }: { reparto: PorPunto[] }) {
  if (reparto.length === 0) {
    return null;
  }

  const mayor = Math.max(...reparto.map((p) => p.total), 1);

  return (
    <div className="mb-4 rounded-2xl border border-armazon-200 bg-white p-4">
      <h3 className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-700">
        <Layers className="size-4 text-armazon-400" aria-hidden />
        Reparto por caja
      </h3>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {reparto.map((p) => (
          <div
            key={p.puntoId}
            className="rounded-xl border border-armazon-100 bg-armazon-50/50 p-3"
          >
            <div className="mb-1 flex items-baseline gap-2">
              <span className="font-mono text-sm font-bold text-armazon-700">{p.codigo}</span>
              <span className="truncate text-[10px] uppercase text-armazon-400">{p.nombre}</span>
            </div>
            <p className="text-lg font-bold tabular-nums text-marca-700">
              {p.emitidas.toLocaleString('es-EC')}
              <span className="ml-1 text-[11px] font-normal text-armazon-400">
                de {p.total.toLocaleString('es-EC')}
              </span>
            </p>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-armazon-100">
              <div
                className="h-full rounded-full bg-marca-500 transition-all duration-700"
                style={{ width: `${(p.total / mayor) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PanelDeCorreos({
  pendientes,
  alProbar,
  probando,
  hayComprobante,
}: {
  pendientes: number;
  alProbar: (destino: string) => void;
  probando: boolean;
  hayComprobante: boolean;
}) {
  const [destino, setDestino] = useState('');

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-armazon-200 bg-white p-6 text-center">
        <Mail className="mx-auto mb-2 size-6 text-armazon-400" aria-hidden />
        <p className="text-3xl font-black tabular-nums text-armazon-700">
          {pendientes.toLocaleString('es-EC')}
        </p>
        <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-armazon-500">
          Comprobantes autorizados sin enviar
        </p>
        <p className="mx-auto mt-3 max-w-md text-[11px] leading-relaxed text-armazon-500">
          EL COMPROBANTE SE ENTREGA CUANDO YA ESTÁ AUTORIZADO, NO ANTES. SE MANDA EL PDF Y EL XML, Y
          CADA UNO SE MARCA AL SALIR: VOLVER A PULSAR SIGUE POR DONDE IBA.
        </p>
      </div>

      <div className="rounded-2xl border border-armazon-200 bg-white p-6">
        <h3 className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-700">
          <Send className="size-4 text-armazon-400" aria-hidden />
          Probar la plantilla
        </h3>
        <p className="mb-4 max-w-lg text-[11px] leading-relaxed text-armazon-500">
          MANDA EL ÚLTIMO COMPROBANTE AUTORIZADO —CON SU RIDE Y SU XML— A LA DIRECCIÓN QUE ESCRIBA.
          SÓLO VA AHÍ: NO TOCA LA DE NINGÚN CLIENTE.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            value={destino}
            onChange={(evento) => setDestino(evento.target.value)}
            placeholder="alguien@epagal.gob.ec"
            aria-label="Dirección a la que mandar la prueba"
            className="h-9 min-w-64 flex-1 rounded-lg border border-armazon-200 px-3 text-xs outline-none focus:border-marca-400"
          />
          <button
            type="button"
            onClick={() => alProbar(destino)}
            disabled={probando || !destino.includes('@') || !hayComprobante}
            title={
              hayComprobante ? undefined : 'Todavía no hay ningún comprobante autorizado que mandar'
            }
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-marca-600 px-4 text-[11px] font-bold uppercase tracking-wide text-white transition hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {probando ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Send className="size-4" aria-hidden />
            )}
            Enviar la prueba
          </button>
        </div>
      </div>
    </div>
  );
}
